import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface EvidenceItem {
  id: number;
  schedule_id: number | null;
  project_id: number | null;
  uploaded_by: number;
  original_name: string;
  mime_type: string | null;
  size: number | null;
  processed_at: number | null;
  processing_attempts: number;
  last_error: string | null;
  url: string;
}

interface EvidenceStats {
  total: number;
  pending: number;
  error: number;
  processed: number;
}

const EvidenceMonitorPage: React.FC = () => {
  const [items, setItems] = useState<EvidenceItem[]>([]);
  const [stats, setStats] = useState<EvidenceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [scheduleIdFilter, setScheduleIdFilter] = useState('');
  const [projectIdFilter, setProjectIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'error' | 'processed'>('all');

  const loadData = async () => {
    setLoading(true);
    setError(null);

    const params: any = { page, pageSize };
    if (scheduleIdFilter) params.schedule_id = scheduleIdFilter;
    if (projectIdFilter) params.project_id = projectIdFilter;

    try {
      const [listRes, statsRes] = await Promise.all([
        apiClient.get<{
          data: { items: EvidenceItem[]; pagination?: { total: number; page: number; pageSize: number } };
        }>('/evidences', {
          params,
        }),
        apiClient.get<{ data: EvidenceStats }>('/evidences/stats', {
          params: { schedule_id: scheduleIdFilter || undefined, project_id: projectIdFilter || undefined },
        }),
      ]);

      const listData = listRes.data.data;
      setItems(listData.items || []);
      if (listData.pagination && typeof listData.pagination.total === 'number') {
        setTotal(listData.pagination.total);
      } else if (Array.isArray(listData.items)) {
        setTotal(listData.items.length);
      }

      setStats(statsRes.data.data);
    } catch (e) {
      setError('No se pudieron cargar las evidencias');
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async (id: number) => {
    try {
      await apiClient.post(`/evidences/${id}/reprocess`);
      await loadData();
    } catch (e) {
      setError('No se pudo reencolar el procesamiento de la evidencia');
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleApplyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const filteredItems = items.filter(ev => {
    if (statusFilter === 'pending') {
      return !ev.processed_at && !ev.last_error;
    }
    if (statusFilter === 'error') {
      return !!ev.last_error;
    }
    if (statusFilter === 'processed') {
      return !!ev.processed_at && !ev.last_error;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Monitor global de evidencias
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Visualiza el estado de todas las evidencias del sistema en tiempo real.
        </p>
      </header>

      {stats && (
        <section
          style={{
            marginBottom: 16,
            padding: 16,
            borderRadius: 18,
            background: 'linear-gradient(135deg, #0f172a, #020617)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              color: '#e5e7eb',
            }}
          >
            Resumen global
          </h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 8,
              fontSize: 12,
            }}
          >
            <span>Total: {stats.total}</span>
            <span>Pendientes: {stats.pending}</span>
            <span>Con error: {stats.error}</span>
            <span>Procesadas: {stats.processed}</span>
          </div>
        </section>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        {/* Filtros */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <h2
            style={{
              margin: '0 0 10px',
              fontSize: 15,
              fontWeight: 600,
              color: '#e5e7eb',
            }}
          >
            Filtros
          </h2>
          <form
            onSubmit={handleApplyFilters}
            style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}
          >
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span>Schedule ID</span>
              <input
                type="number"
                value={scheduleIdFilter}
                onChange={e => setScheduleIdFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 10,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 12,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span>Project ID</span>
              <input
                type="number"
                value={projectIdFilter}
                onChange={e => setProjectIdFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 10,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 12,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span>Estado</span>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 10,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 12,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="all">Todos</option>
                <option value="pending">Solo pendientes</option>
                <option value="error">Solo con error</option>
                <option value="processed">Solo procesadas</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 6,
                padding: '8px 12px',
                borderRadius: 999,
                border: 'none',
                fontSize: 12,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  loading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
              }}
            >
              {loading ? 'Cargando...' : 'Aplicar filtros'}
            </button>
          </form>
        </section>

        {/* Listado */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de evidencias
            </h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Total página: {filteredItems.length}</span>
          </div>

          {loading && <p style={{ fontSize: 12 }}>Cargando evidencias...</p>}
          {error && (
            <div style={{ color: '#fecaca', marginBottom: 8, fontSize: 12 }}>{error}</div>
          )}
          {!loading && filteredItems.length === 0 && (
            <p style={{ fontSize: 12 }}>No hay evidencias que coincidan con los filtros.</p>
          )}
          {!loading && filteredItems.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th style={{ padding: '6px 4px' }}>Schedule</th>
                    <th style={{ padding: '6px 4px' }}>Proyecto</th>
                    <th style={{ padding: '6px 4px' }}>Nombre</th>
                    <th style={{ padding: '6px 4px' }}>MIME</th>
                    <th style={{ padding: '6px 4px' }}>Tamaño</th>
                    <th style={{ padding: '6px 4px' }}>Estado</th>
                    <th style={{ padding: '6px 4px' }}>Intentos</th>
                    <th style={{ padding: '6px 4px' }}>Error</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map(ev => (
                    <tr
                      key={ev.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{ev.id}</td>
                      <td style={{ padding: '6px 4px' }}>{ev.schedule_id ?? '-'}</td>
                      <td style={{ padding: '6px 4px' }}>{ev.project_id ?? '-'}</td>
                      <td style={{ padding: '6px 4px' }}>{ev.original_name}</td>
                      <td style={{ padding: '6px 4px' }}>{ev.mime_type ?? '-'}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {ev.size != null ? `${(ev.size / 1024).toFixed(1)} KB` : '-'}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {ev.last_error
                          ? 'Error'
                          : ev.processed_at
                          ? 'Procesada'
                          : 'Pendiente'}
                      </td>
                      <td style={{ padding: '6px 4px' }}>{ev.processing_attempts}</td>
                      <td
                        style={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          padding: '6px 4px',
                        }}
                      >
                        {ev.last_error ?? '-'}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <a
                          href={ev.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#38bdf8', textDecoration: 'none', marginRight: 4 }}
                        >
                          Ver/descargar
                        </a>
                        {ev.last_error && (
                          <button
                            type="button"
                            onClick={() => handleReprocess(ev.id)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(59, 130, 246, 0.7)',
                              backgroundColor: 'rgba(30, 64, 175, 0.35)',
                              color: '#bfdbfe',
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            Reprocesar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              marginTop: 12,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              style={{
                padding: '4px 8px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                cursor: page <= 1 || loading ? 'default' : 'pointer',
              }}
            >
              {'<'}
            </button>
            <span>
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              style={{
                padding: '4px 8px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                cursor: page >= totalPages || loading ? 'default' : 'pointer',
              }}
            >
              {'>'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default EvidenceMonitorPage;
